import { useState, useEffect, useCallback, useRef } from 'react';

import useShoppingBasketCart from '@src/hooks/useShoppingBasketCart';
import useCustomerWishlist from '@src/hooks/useCustomerWishlist';
import useLocalWishlistStore, { localWishlistStore } from '@src/stores/localWishlistStore';

import { ICartItem } from '@src/types/cart';

export interface IRemovedCartItem extends ICartItem {
  removedAt: number;
  initialWishlistState: boolean;
}

/**
 * Custom hook to handle cart item removal with undo functionality
 * Manages temporarily removed items and provides undo/wishlist actions
 */
export const useCartItemRemoval = () => {
  const shopifyCustomerId = (window as any).customer?.id;
  const { updateItemAndFetch, addToCartAndFetch, isUpdatingItem } = useShoppingBasketCart();
  const { updateCustomerList, createCustomerList, fetchCustomerLists, isProductInWishlist } = useCustomerWishlist();
  const updateLocalList = useLocalWishlistStore((state) => state.updateLocalList);

  // State for managing temporarily removed items
  const [tempRemovedItems, setTempRemovedItems] = useState<IRemovedCartItem[]>([]);

  // Use ref to store timeouts to avoid stale closures
  const undoTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

  /**
   * Handle removing an item from cart with undo option
   */
  const handleRemoveItem = useCallback(
    async (item: ICartItem) => {
      if (isUpdatingItem) return;

      // Store the item for potential undo with a unique identifier
      const removedAt = Date.now();
      const removedItem: IRemovedCartItem = {
        ...item,
        removedAt, // Add timestamp as unique identifier
        initialWishlistState: isProductInWishlist(item.product_id) // Capture initial wishlist state
      };

      setTempRemovedItems((prev) => [...prev, removedItem]);

      // Actually remove from cart immediately
      await updateItemAndFetch(
        {
          key: item.key,
          quantity: 0
        },
        true
      );

      // Set up timeout to clear the undo option after 10 seconds
      const timeoutId = setTimeout(() => {
        // Clean up the undo option
        setTempRemovedItems((prev) => prev.filter((tempItem) => tempItem.removedAt !== removedAt));
        undoTimeoutsRef.current.delete(removedAt);
      }, 10000); // 10 seconds

      // Store timeout reference using the timestamp
      undoTimeoutsRef.current.set(removedAt, timeoutId);
    },
    [isUpdatingItem, updateItemAndFetch]
  );

  /**
   * Handle undoing the removal of an item
   */
  const handleUndoRemove = useCallback(
    async (item: IRemovedCartItem) => {
      // Clear the timeout
      const timeout = undoTimeoutsRef.current.get(item.removedAt);
      if (timeout) {
        clearTimeout(timeout);
      }

      // Add the item back to the cart using variant ID
      await addToCartAndFetch(
        [
          {
            variantId: item.variant_id,
            quantity: item.quantity
          }
        ],
        true
      );

      // Remove from temporarily removed items
      setTempRemovedItems((prev) => prev.filter((removedItem) => removedItem.removedAt !== item.removedAt));

      // Clean up timeout reference
      undoTimeoutsRef.current.delete(item.removedAt);
    },
    [addToCartAndFetch]
  );

  /**
   * Handle adding a removed item to wishlist
   */
  const handleUndoWishlist = useCallback(
    async (item: IRemovedCartItem) => {
  const localLists = localWishlistStore.getState().localLists;
      const currentList = localLists.find((list) => list.listType === 'products');
      const isCurrentlyInWishlist = isProductInWishlist(item.product_id);

      let newProducts: string[] = [];

      if (isCurrentlyInWishlist) {
        // Remove from wishlist
        newProducts =
          currentList?.products.filter((productId) => !productId.includes(item.product_id.toString())) || [];
      } else {
        // Add to wishlist
        newProducts = [...(currentList?.products || []), `gid://shopify/Product/${item.product_id}`];
      }

      const listName = 'Products';
      const listType = 'products';

      try {
        // If it's logged in customer, update metaobject & metafield
        if (shopifyCustomerId) {
          // Immediately get the returned wishlist metaobject data
          // to use the metaobject ID to update customer's metafield
          const updateListRes = await updateCustomerList({
            handle: `${shopifyCustomerId}-${listType}`,
            listName,
            products: newProducts
          });

          // Add the metaobject to customer's metafield
          // if the currentList doesn't exist
          if (!currentList) {
            const fetchCustomerListsRes = await fetchCustomerLists({ customerId: shopifyCustomerId });
            const newListIds = [...(fetchCustomerListsRes?.map((wishlist) => wishlist.id) || []), updateListRes.id];

            createCustomerList({ customerId: shopifyCustomerId, wishlistLists: newListIds });
          }
        }

        // Store in local storage
        updateLocalList({ listName, listType, products: newProducts });
      } catch (error) {
        console.error('Failed to toggle wishlist:', error);
      }
    },
    [
      updateCustomerList,
      createCustomerList,
      fetchCustomerLists,
      isProductInWishlist,
      updateLocalList,
      shopifyCustomerId
    ]
  );

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      undoTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      undoTimeoutsRef.current.clear();
    };
  }, []);

  return {
    tempRemovedItems,
    handleRemoveItem,
    handleUndoRemove,
    handleUndoWishlist,
    isProductInWishlist
  };
};

export default useCartItemRemoval;
